import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { api, type User } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<{ needsEmailConfirmation?: boolean }>;
  guestLogin: () => Promise<void>;
  startOAuth: (provider: 'google' | 'github' | 'facebook' | 'twitter') => Promise<void>;
  updateUserName: (name: string) => Promise<void>;
  updateUserPicture: (file: File) => Promise<string>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const AUTH_TIMEOUT_MS = 10000; // 10秒で強制的にローディング終了

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadingResolved = useRef(false);
  const profileLoading = useRef(false);

  const finishLoading = useCallback(() => {
    if (!loadingResolved.current) {
      loadingResolved.current = true;
      setIsLoading(false);
    }
  }, []);

  // Fetch profile and set user state from Supabase auth user
  const loadProfile = useCallback(async (accessToken: string, authUserId?: string) => {
    if (profileLoading.current) return;
    profileLoading.current = true;
    setToken(accessToken);
    if (authUserId) {
      api.setUserId(authUserId);
    }
    try {
      const me = await api.getMe(accessToken);
      setUser(me);
    } catch {
      setUser(null);
      setToken(null);
    } finally {
      profileLoading.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setToken(null);
    setUser(null);
  }, []);

  // Listen for auth state changes
  // Supabase v2 の onAuthStateChange は INITIAL_SESSION イベントで初期セッションを通知する。
  // OAuth PKCE フローの場合: _initialize() がコード交換を完了後に SIGNED_IN → INITIAL_SESSION の順で発火。
  // 通常訪問の場合: localStorage からセッション復元後に INITIAL_SESSION が発火。
  // initSession() は不要 — onAuthStateChange が全ケースをカバーする。
  useEffect(() => {
    // 安全タイムアウト: 認証イベントが一切来ない場合のみ発火
    // SIGNED_IN/INITIAL_SESSION を受信したらキャンセルし、loadProfileに必要な時間を与える
    let timeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      if (!loadingResolved.current) {
        console.warn('[Auth] Loading timeout — forcing isLoading=false');
        api.notifyAuthFailed();
        finishLoading();
      }
    }, AUTH_TIMEOUT_MS);

    const cancelTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.debug('[Auth] onAuthStateChange:', event, !!session);
        if (event === 'SIGNED_IN' && session) {
          cancelTimeout();
          await loadProfile(session.access_token, session.user?.id);
          finishLoading();
        } else if (event === 'INITIAL_SESSION') {
          if (session) {
            cancelTimeout();
            await loadProfile(session.access_token, session.user?.id);
            finishLoading();
          } else {
            // URL に code= がある場合、PKCE コード交換中なので SIGNED_IN を待つ
            const hasAuthCode = window.location.search.includes('code=') ||
              window.location.hash.includes('access_token=');
            if (hasAuthCode) {
              console.debug('[Auth] INITIAL_SESSION without session, but auth code detected — waiting for SIGNED_IN');
            } else {
              api.notifyAuthFailed();
              finishLoading();
            }
          }
        } else if (event === 'PASSWORD_RECOVERY' && session) {
          cancelTimeout();
          setToken(session.access_token);
          finishLoading();
          // パスワードリセットページにリダイレクト
          window.location.replace('/auth/reset');
        } else if (event === 'TOKEN_REFRESHED' && session) {
          setToken(session.access_token);
          if (session.user?.id) api.setUserId(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          api.clearCache();
          setUser(null);
          setToken(null);
          finishLoading();
        }
      },
    );

    return () => {
      cancelTimeout();
      subscription.unsubscribe();
    };
  }, [loadProfile, finishLoading]);

  const login = async (email: string, password: string) => {
    const res = await api.login(email, password);
    setToken(res.token);
    setUser(res.user);
  };

  const register = async (email: string, password: string, name: string) => {
    const res = await api.register(email, password, name);
    if (res.needsEmailConfirmation) {
      return { needsEmailConfirmation: true };
    }
    setToken(res.token);
    setUser(res.user);
    return {};
  };

  const guestLogin = async () => {
    const res = await api.guestLogin();
    setToken(res.token);
    setUser(res.user);
  };

  const updateUserName = useCallback(async (name: string) => {
    await api.updateProfile({ display_name: name });
    setUser(prev => prev ? { ...prev, name } : prev);
  }, []);

  const updateUserPicture = useCallback(async (file: File) => {
    const url = await api.uploadAvatar(file);
    setUser(prev => prev ? { ...prev, picture: url } : prev);
    return url;
  }, []);

  const startOAuth = useCallback(async (provider: 'google' | 'github' | 'facebook' | 'twitter') => {
    // 古い PKCE ステート (code_verifier) をクリーンアップ
    // 前回の OAuth が途中で失敗した場合に残留し、次回のリクエストを汚染する可能性がある
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('-code-verifier') || key.includes('pkce'))) {
          keysToRemove.push(key);
        }
      }
      if (keysToRemove.length > 0) {
        console.debug('[Auth] Cleaning stale PKCE state:', keysToRemove);
        keysToRemove.forEach(k => localStorage.removeItem(k));
      }
    } catch {
      // localStorage アクセス失敗は無視
    }

    console.debug(`[Auth] Starting OAuth: ${provider}, redirectTo: ${window.location.origin}/`);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/`,
        skipBrowserRedirect: false,
      },
    });
    if (error) {
      console.error(`[Auth] OAuth error (${provider}):`, error.message, error);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, register, guestLogin, startOAuth, updateUserName, updateUserPicture, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
